import { ProgressEvent } from '../../../adapters';
import { HistogramBucket, SpanTypes, WindowEvents } from '../../../analytics';
import {
    ContentDuration,
    IContent,
    IPlaybackStats,
    MediaControllerState,
    PlanetMediaPlayer,
    PlanetUserInteractionArgs,
    PlaybackSessionState,
    TimeSpan,
} from '../../../media-player';
import {
    EventEnvelopData,
    EventKey,
    EventStream,
    Guid,
    IPlaybackSegmentContext,
    IPlaybackSessionContext,
} from '../../../utils';
import { VideoAnalyticsTrackerType } from './enums';
import { PlanetAnalyticsTrackerOptions } from './models';
import { PlaybackSessionTracker } from './playback-session-tracker';

export class PlanetAnalyticsTracker extends PlaybackSessionTracker<VideoAnalyticsTrackerType.Garavot, PlanetAnalyticsTrackerOptions> {

    private playerEvents: EventStream;
    private currentPlaybackSegment: EventStream;
    private playbackSessionContext: EventStream;
    private impressionContext: EventStream;
    private playbackSession: PlaybackSessionState;

    async setup(player: PlanetMediaPlayer): Promise<PlanetAnalyticsTracker> {
        await super.setup(player, {});
        this.playerEvents = EventStream.getStream(SpanTypes.Player, this.player.getId());
        window.addEventListener(WindowEvents.BeforeUnload, () => {
            this.dispose();
        });
        return this;
    }

    async trackSeek(position: number) {
        // in live streams the player will try to seek to the live edge
        // we avoid tracking this event since it happens before the playback
        // session is created.
        if (this.playbackSessionContext) {
            this.playbackSessionContext.append(EventKey.PlaybackSessionSeeked, {
                playbackSessionId: this.playbackSession.id,
                position: position,
            });
        }
    }

    trackSessionCreate(session: PlaybackSessionState): void {
        this.playbackSession = session;
    }

    trackContentLoad(content: IContent, index: number): void {
        const impressionId: string = Guid.v1();

        if (this.impressionContext) {
            this.impressionContext.end();
            this.impressionContext.flush(true);
        }

        this.impressionContext = this.playerEvents.createStream(SpanTypes.ContentImpression, impressionId, {
            id: impressionId,
            content: content,
        }).start();

        this.impressionContext.append(EventKey.PlaybackSessionContentLoaded, {
            playbackSessionId: this.playbackSession.id,
            autoplay: this.player.getConfig().autostart,
            content: content,
            index: index,
        });
    }

    trackPlaybackStart(position: number, duration: ContentDuration): void {
        const id: string = Guid.v1();
        const stats: IPlaybackStats = this.player.getPlaybackStats();
        const session: PlaybackSessionState = this.playbackSession;
        let buckets: HistogramBucket[] = [];

        if (session.playbackHistogram && session.currentSegment) {
            buckets = [...session.playbackHistogram.map(session.currentSegment)];
        }

        this.playbackSessionContext = this.impressionContext.createStream(SpanTypes.PlaybackSession, session.id, {
            playbackSession: {
                id: session.id,
            } as IPlaybackSessionContext,
        });
        this.playbackSessionContext.start();

        this.playbackSessionContext.append(EventKey.PlaybackSessionCreated, {
            playbackSession: {id: session.id},
        });

        this.currentPlaybackSegment = this.playbackSessionContext.createStream(SpanTypes.PlaybackSegment, id, {
            playbackSegment: {
                id: id,
                startPosition: session.currentSegment.start,
                endPosition: session.currentSegment.end,
                duration: session.currentSegment.duration,
                buckets: buckets,
                ended: false,
            } as IPlaybackSegmentContext,
            rendition: stats ? stats.currentRendition : {
                id: 'unknown',
                bitrate: 0,
                height: 0,
                width: 0,
            },
        }).start();

        this.playbackSessionContext.append(EventKey.PlaybackSessionStarted, {
            playerId: this.player.getId(),
            playbackSessionId: session.id,
            autoplay: this.player.getConfig().autostart,
            position: position,
        });
    }

    trackPlaybackPause(position: number, duration: ContentDuration, state: MediaControllerState, segment: TimeSpan): void {
        if (!this.currentPlaybackSegment) {
            return;
        }
        this.currentPlaybackSegment.inject({
            playbackSegment: {
                startPosition: segment.start,
                endPosition: segment.end,
                duration: segment.duration,
                buckets: segment.buckets,
                ended: true,
            } as IPlaybackSegmentContext,
        });
        this.currentPlaybackSegment.end();

        this.playbackSessionContext.append(EventKey.PlaybackSessionPaused, {
            playerId: this.player.getId(),
            playbackSessionId: this.playbackSession.id,
            position: position,
        });
    }

    trackPlaybackResume(position: number, duration: ContentDuration): void {
        const id: string = Guid.v1();
        const stats: IPlaybackStats = this.player.getPlaybackStats();
        const session: PlaybackSessionState = this.player.getPlaybackSession();

        const context = {
            playbackSegment: {
                id: id,
                startPosition: session.currentSegment.start,
                endPosition: session.currentSegment.end,
                duration: session.currentSegment.duration,
                ended: true,
            },
            rendition: stats ? stats.currentRendition : {
                id: 'unknown',
                bitrate: 0,
                height: 0,
                width: 0,
            },
        };

        this.currentPlaybackSegment = this.playbackSessionContext.createStream(SpanTypes.PlaybackSegment, id, {
            ...context,
            duration: 0,
            ended: false,
        }).start();

        this.playbackSessionContext.append(EventKey.PlaybackSessionResumed, {
            playbackSessionId: this.playbackSession.id,
            position: position,
        });
    }

    trackSessionEnd(session: PlaybackSessionState): void {
        if (this.playbackSessionContext) {
            this.playbackSessionContext.append(EventKey.PlaybackSessionEnded, {
                playbackSession: {id: session.id},
            });
            this.playbackSessionContext.end();
            this.playbackSessionContext.flush(true);
        }
    }

    trackPlaybackComplete(): void {
    }

    trackStateChange(oldState: MediaControllerState, newState: MediaControllerState): void {
    }

    dispose(): void {
        this.playerEvents.flush();
    }

    onUserInteraction(event: PlanetUserInteractionArgs): void {
    }

    onError(error: Error): void {
        console.warn('[PlanetAnalyticsTracker] Error', error.message);
    }

    onSetupError(setupError: Error): void {
        console.warn('[PlanetAnalyticsTracker] Setup Error', setupError.message);
    }

    onBufferChange(data: ProgressEvent): void {
    }

    private async send(type: EventKey, data: EventEnvelopData, immediate: boolean = false): Promise<void> {
        const playerId: string = this.player.getId();
        if (immediate) {
            await this.playerEvents.appendAndFlush(type, data, {playerId});
        } else {
            this.playerEvents.append(type, data, {playerId});
        }
    }
}
